import { useFormik, FormikHelpers } from "formik";
import { Lock } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../../../(components)/ui/input";
import TermsAndConditions from "../../../(components)/ui/TermsAndConditions";
import ToastSuccess from "../../../../(common)/Toast/ToastSuccess";
import ToastWarning from "../../../../(common)/Toast/ToastWarning";
import {
  StyledButton,
  buttonStyles,
} from "../../../../../Shared/button/button";
import { TEXTS } from "../../../constants/texts";

import { callApi } from "../../../lib/api";
import { sendSignupOtp } from "../../../../../utils/otpApi";
import { emailSchema } from "../../../constants/schema";

export interface FormValuesStep1 {
  email: string;
  agreeToTerms: boolean;
}

const Step1 = ({
  setCreatedUserData,
  setStep,
}: {
  setCreatedUserData: React.Dispatch<React.SetStateAction<any>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const formik = useFormik<FormValuesStep1>({
    initialValues: {
      email: "",
      agreeToTerms: true,
    },
    validationSchema: emailSchema,
    onSubmit: async (
      values: FormValuesStep1,
      { setSubmitting }: FormikHelpers<FormValuesStep1>
    ) => {
      await handleCreateUser(values);
      setSubmitting(false);
    },
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: false,
  });

  const handleCreateUser = async (values: FormValuesStep1) => {
    try {
      setLoading(true);
      const res = await sendSignupOtp(callApi, values.email, values.agreeToTerms);

      setCreatedUserData({
        relayId: res.relay_id,
        email: values.email,
        token: res?.token,
      });

      ToastSuccess(values.email, [
        TEXTS.SIGNUP_OTP_SENT,
        TEXTS.SIGNUP_OTP_MESSAGE,
      ]);
      setStep(2);
    } catch (err: any) {
      ToastWarning("", err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="d-flex flex-column shadow bg-white border rounded-4 px-4 py-4"
      style={{
        width: 400,
        boxShadow:
          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        transition: "width 0.3s ease", // optional: smooth transition
      }}
    >
      <p className="text-left fs-3 fw-semibold text-primary-100">
        Create an Account
      </p>
      <Input
        id="signup-email"
        type="text"
        name="email"
        placeholder="Enter Business email"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
      {formik.errors.email && (
        <div className="text-xs text-danger d-block">{formik.errors.email}</div>
      )}

      <TermsAndConditions formik={formik} />
      <StyledButton
        id="signup-email-submit"
        className="my-2"
        type="submit"
        style={
          formik.isSubmitting || loading || !formik.isValid
            ? buttonStyles.disabled.normal
            : buttonStyles.primary.normal
        }
        hoverStyle={
          formik.isSubmitting || loading || !formik.isValid
            ? buttonStyles.disabled.hover
            : buttonStyles.primary.hover
        }
        disabled={
          !formik.isValid || !formik.dirty || formik.isSubmitting || loading
        }
        isLoading={loading}
      >
        {loading ? "Please wait..." : "Continue"}
      </StyledButton>

      <div className="text-center text-typo-titles-H2  text-sm mt-3  align-items-center justify-content-center d-flex gap-1">
        <Lock size={14} />
        Your data is secure with us
      </div>
      <div className="d-flex align-items-center my-3">
        <hr className="flex-grow-1" />
        <span className="mx-2 text-typo-titles-H2 text-sm">or</span>
        <hr className="flex-grow-1" />
      </div>

      <StyledButton
        className="text-typo-h1 rounded-2 text-base cursor-pointer bg-white border border-typo-h1 w-100 py-2"
        type="button"
        onClick={() => navigate("/login")}
        id="already-have-account-link"
        style={buttonStyles.secondary.normal}
        hoverStyle={buttonStyles.secondary.hover}
      >
        Already have an account? Login
      </StyledButton>
    </form>
  );
};

export default Step1;
